import React from 'react'
import PropTypes from 'prop-types'
import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Toolbar,
  Typography,
} from '@material-ui/core'
import { withStyles } from '@material-ui/core/styles'

import * as CommunityActionCreators from '../../actions/community'
import * as DoorAccessActionCreators from '../../actions/doorAccess'

import GrantDoorAccessButton from './GrantDoorAccessButtonContainer'
import CommunityGrantDoorAccessDialog from './CommunityGrantDoorAccessDialog'
import DeleteIconButton from '../controls/buttons/DeleteIconButton'
import Link from '../controls/Link'

import idObjectMapper from '../../utils/IdObjectMapper'
import { possessify } from '../../utils/String'

class DoorAccessAccessListContainer extends React.Component {
  static propTypes = {
    /* HOC/middleware props */
    classes: PropTypes.object.isRequired,
    /* passed props */
    accessId: PropTypes.number.isRequired,
    communityId: PropTypes.number.isRequired,
    /* mapped state */
    doorAccesses: PropTypes.object.isRequired,
    doorAccessesByCommunity: PropTypes.object.isRequired,
    doorAccessesByUnit: PropTypes.object.isRequired,
    unitsByCommunity: PropTypes.object.isRequired,
    units: PropTypes.object.isRequired,
    /* mapped dispatch */
    doorAccessActions: PropTypes.object.isRequired,
    communityActions: PropTypes.object.isRequired,
  }

  static mapStateToProps(state) {
    return {
      doorAccesses: state.doorAccesses,
      doorAccessesByCommunity: state.doorAccessesByCommunity,
      doorAccessesByUnit: state.doorAccessesByUnit,
      unitsByCommunity: state.unitsByCommunity,
      units: state.units,
    }
  }

  static mapDispatchToProps(dispatch) {
    return {
      communityActions: bindActionCreators(CommunityActionCreators, dispatch),
      doorAccessActions: bindActionCreators(DoorAccessActionCreators, dispatch),
    }
  }

  static styles = () => ({
    toolbar: {
      minHeight: 0,
      padding: 0,
    },
    toolbarControls: {
      flex: '1',
      textAlign: 'right',
    },
    table: {
      '& th:last-child': {
        textAlign: 'right',
        paddingRight: 10,
      },
      '& td:last-child': {
        textAlign: 'right',
        paddingRight: 0,
      },
    },
  })

  componentDidUpdate(prevProps) {
    const { communityId, unitsByCommunity } = this.props
    const prevUnitIds = prevProps.unitsByCommunity[communityId] || []
    const unitIds = unitsByCommunity[communityId] || []

    if (prevProps.communityId !== communityId) {
      this.props.communityActions.fetchCommunity(communityId)
      this.props.communityActions.listCommunityUnits(communityId)
      this.props.doorAccessActions.listCommunityDoorAccessIds(communityId)
    }

    if (!prevUnitIds.length && unitIds.length) {
      unitIds.forEach((unitId) => {
        this.props.doorAccessActions.listUnitDoorAccessIds(unitId)
      })
    }
  }

  handleRemoveCommunityAccess = () => {
    const { accessId, communityId, doorAccessActions } = this.props
    doorAccessActions.removeCommunityDoorAccess(communityId, accessId)
  }

  handleRemoveUnitAccess = (unitId) => {
    this.props.doorAccessActions.removeUnitDoorAccess(unitId, this.props.accessId)
  }

  doorAccess() {
    return this.props.doorAccesses[this.props.accessId] || {}
  }

  hasCommunityWideAccess() {
    const { accessId, communityId, doorAccessesByCommunity } = this.props
    const communityWideAccessIds = doorAccessesByCommunity[communityId] || []
    return communityWideAccessIds.includes(accessId)
  }

  accessibleUnits() {
    const { accessId, doorAccessesByUnit, units } = this.props

    const accessibleUnitIds = Object
      .entries(doorAccessesByUnit)
      // eslint-disable-next-line no-unused-vars
      .filter(([unitId, accessIds]) => accessIds.includes(accessId))
      // eslint-disable-next-line no-unused-vars
      .map(([unitId, accessIds]) => unitId)

    return idObjectMapper(accessibleUnitIds, units)
  }

  renderCommunityWideAccess = () => {
    if (this.hasCommunityWideAccess()) {
      return (
        <TableRow key="community">
          <TableCell>
            <Link to={`/communities/${this.props.communityId}`}>Community - wide</Link>
          </TableCell>
          <TableCell>
            <DeleteIconButton
              onClick={this.handleRemoveCommunityAccess}
              tooltipTitle="Remove community - wide access"
            />
          </TableCell>
        </TableRow>
      )
    }
    return null
  }

  renderAccessibleUnits = () => (
    this.accessibleUnits().map((unit) => (
      <TableRow key={`unit-${unit.id}`}>
        <TableCell>
          <Link to={`/units/${unit.id}`}>{unit.name}</Link>
        </TableCell>
        <TableCell>
          <DeleteIconButton
            onClick={() => { this.handleRemoveUnitAccess(unit.id) }}
            tooltipTitle={`Remove Access to ${unit.name}`}
          />
        </TableCell>
      </TableRow>
    ))
  )

  render() {
    const { accessId, classes, communityId } = this.props
    const doorAccess = this.doorAccess()

    if (doorAccess.id) { // if we have data
      return (
        <>
          <Toolbar className={classes.toolbar}>
            <Typography variant="h6">
              { possessify(doorAccess.name) } Access
            </Typography>
            <div className={classes.toolbarControls}>
              <GrantDoorAccessButton accessIds={[accessId]} />
            </div>
          </Toolbar>

          <Table className={classes.table}>
            <TableHead>
              <TableRow>
                <TableCell>Door</TableCell>
                <TableCell>Remove Access</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              { this.renderCommunityWideAccess() }
              { this.renderAccessibleUnits() }
            </TableBody>
          </Table>

          <CommunityGrantDoorAccessDialog communityId={communityId} />
        </>
      )
    }

    return null
  }
}

export default connect(
  DoorAccessAccessListContainer.mapStateToProps,
  DoorAccessAccessListContainer.mapDispatchToProps,
)(withStyles(DoorAccessAccessListContainer.styles)(DoorAccessAccessListContainer))
